import React, { useMemo } from 'react';

import {
  Account,
  Allowance,
  Capabilities,
  EditorEnum,
  Repository,
} from 'src/components/pipelines/models';

import DeploymentsLogo from '../../../assets/deployments-logo.png';
import { ORIGIN } from '../const';
import { OnboardingType } from '../types';
import { EditorTypeToAnalyticSourceMap } from '../utils/util';

import { OnboardingContextProvider } from './OnboardingContext';
import {
  Center,
  DeploymentsWrapper,
  PlanInfo,
  StyledDeployments,
} from './styled';
import TwoStepVerification from './TwoStepVerification';

type Props = {
  account: Account;
  allowance: Allowance;
  capabilities: Capabilities;
  repository: Repository;
};

const DeploymentsOnboarding = ({
  account,
  allowance,
  capabilities,
  repository,
}: Props) => {
  const analyticsSource =
    EditorTypeToAnalyticSourceMap[EditorEnum.DeploymentsOnboarding];

  const getQuota = useMemo(() => {
    return allowance.quota !== -1 && allowance.quota.toLocaleString();
  }, [allowance.quota]);

  const getPlansPageUrl = useMemo(() => {
    const workspaceSlug = repository.getWorkplaceSlug();
    return `${ORIGIN}/${workspaceSlug}/workspace/settings/plans-and-billing/`;
  }, [repository]);

  const renderPlanInfo = () => {
    return (
      <PlanInfo>
        {getQuota ? (
          <>
            Deployments is powered by Pipelines, which you can configure below.
            Your current plan includes{' '}
            <a href={getPlansPageUrl} target="_top">
              {getQuota} free build minutes
            </a>{' '}
            per month.
          </>
        ) : (
          <>
            Deployments is powered by Pipelines, which you can configure below.{' '}
            <a href={getPlansPageUrl} target="_top">
              Every Bitbucket plan
            </a>{' '}
            includes free build minutes.
          </>
        )}
      </PlanInfo>
    );
  };

  if (!allowance.isTrusted && !account.userHas2FaEnabled) {
    return (
      <OnboardingContextProvider analyticsSource={analyticsSource}>
        <TwoStepVerification onboardingType={OnboardingType.DEPLOYMENTS} />
      </OnboardingContextProvider>
    );
  }

  return (
    <DeploymentsWrapper>
      <Center>
        <StyledDeployments src={DeploymentsLogo} alt="Deployment" />
        <h3>Deployment tracking with Bitbucket</h3>
      </Center>
      <div>
        <Center>
          <p>
            {capabilities.pipelinesEnabled ? (
              <>
                Enable deployment tracking to view the status of your
                deployments and key information about every commit you deploy to
                your environments.
              </>
            ) : (
              <>
                Enable deployment tracking to view the status of your
                deployments and key information about every commit you deploy to
                your environments. <br />
                {renderPlanInfo()}
                <br />
              </>
            )}
          </p>
        </Center>
      </div>
    </DeploymentsWrapper>
  );
};

export default React.memo(DeploymentsOnboarding);
